import CircleFormController from '@/circle/components/CircleFormController'
import MeetingOriginBanner from '@/meeting/components/MeetingOriginBanner'
import MemberByIdButton from '@/member/components/MemberByIdButton'
import MemberSearchButton from '@/search/components/MemberSearchButton'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import useCanPropose from '@/proposal/hooks/useCanPropose'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { useNavigateOrg } from '@/org/hooks/useNavigateOrg'
import ParticipantsCircleExtraMembers from '@/participants/components/ParticipantsCircleExtraMembers'
import useOrgMember from '@/member/hooks/useOrgMember'
import VisibilityField from '@/visibility/components/VisibilityField'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  UseModalProps,
  VStack,
} from '@chakra-ui/react'
import {
  ThreadFragment,
  Thread_Activity_Type_Enum,
  Thread_Status_Enum,
  Thread_Type_Enum,
  Visibility_Enum,
  useCreateThreadExtraMemberMutation,
  useCreateThreadMutation,
  useDeleteThreadExtraMemberMutation,
  useUpdateThreadMutation,
} from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  defaultVisibility,
  getVisibilityAccess,
} from '@rolebase/shared/model/visibility'
import { nameSchema } from '@rolebase/shared/schemas'
import React, { useContext, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { track } from 'src/analytics'
import * as yup from 'yup'
import { ThreadContext } from '../contexts/ThreadContext'
import { ThreadStatusMenu } from '../components/ThreadStatusMenu'
import ThreadTypeButtons from '../components/ThreadTypeButtons'

interface Props extends UseModalProps {
  defaults?: Partial<ThreadFragment>
  thread?: ThreadFragment
  // Optional text explaining why this thread is being created,
  // shown at the top of the modal when the caller provides one.
  intro?: string
  onCreate?(threadId: string): void
}

interface Values {
  title: string
  circleId: string
  // The member the tension is attributed to (#89). The column is named
  // `initiatorMemberId`, but the interface has always shown it as the member
  // who carries the tension — that is the selector of the meeting's quick form,
  // and it drives the by-member round (REU89-D11, D13).
  initiatorMemberId: string
  status: Thread_Status_Enum
  type: Thread_Type_Enum | undefined
  visibility: Visibility_Enum
}

const resolver = yupResolver(
  yup.object().shape({
    title: nameSchema.required(),
    circleId: yup.string().required(),
    initiatorMemberId: yup.string().required(),
    type: yup
      .mixed<Thread_Type_Enum>()
      .oneOf(Object.values(Thread_Type_Enum))
      .required(),
  })
)

export default function ThreadEditModal({
  defaults,
  thread,
  intro,
  onCreate,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const navigateOrg = useNavigateOrg()
  const { orgId, orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const [updateThread] = useUpdateThreadMutation()
  const [createThread] = useCreateThreadMutation()
  const [createThreadExtraMember] = useCreateThreadExtraMemberMutation()
  const [deleteThreadExtraMember] = useDeleteThreadExtraMemberMutation()

  const formMethods = useForm<Values>({
    resolver,
    defaultValues: thread
      ? {
          title: thread.title,
          circleId: thread.circleId,
          initiatorMemberId: thread.initiatorMemberId,
          status: thread.status,
          type: thread.type,
          visibility: thread.visibility,
        }
      : {
          title: '',
          circleId: defaults?.circleId || '',
          // Unchanged at creation: the author carries their own tension, and
          // it is corrected afterwards if someone else takes it up (REU89-D13).
          initiatorMemberId: currentMember?.id || '',
          status: Thread_Status_Enum.Active,
          // No default type at creation: it must be explicitly chosen.
          // Exception: the caller may name the type when the gesture itself
          // names it (e.g. "create a governance sub-thread").
          type: defaults?.type,
          // A tension is born at the Circle level: its circle and, for a circle
          // or a link, the circle above (CONF-02, defaultVisibility).
          visibility: defaults?.visibility ?? defaultVisibility.thread,
        },
  })

  const {
    handleSubmit,
    register,
    watch,
    control,
    formState: { errors },
  } = formMethods

  // Watch selected circle
  const circleId = watch('circleId')

  // Watch selected type: required, no default at creation
  const type = watch('type')

  // Extra members ids
  const [extraMembersIds, setExtraMembersIds] = useState<string[]>(
    () => thread?.extra_members.map((em) => em.memberId) || []
  )

  // Visibility: the level chosen must leave the author able to modify what
  // they save. Display rule only, the Hasura permission decides (CONF-02).
  // Only extra members ALREADY saved count as named: on update, the new ones
  // are inserted after the thread, so the server check does not see them yet
  // (review of 14/09/2026).
  const visibility = watch('visibility')
  const isOrgWriter = useOrgMember()
  const isVisibilityAllowed =
    !circleId ||
    !orgData ||
    getVisibilityAccess({
      orgData,
      circleId,
      visibility,
      memberId: currentMember?.id,
      namedMemberIds: thread?.extra_members.map((em) => em.memberId) ?? [],
      isOrgWriter,
    }).canWrite

  // A tension that carries a proposal can no longer be closed off (GOUV-16,
  // D6). Any proposal counts, resolved or cancelled included: those who decided
  // it must keep being able to reread what they decided, and a switch that came
  // back on its own the day the last proposal ended would make no sense to
  // whoever met it.
  //
  // Read from the thread context rather than queried: the two callers that EDIT
  // an existing tension are mounted inside the thread, while the two that create
  // one carry no proposal by construction. A query of its own would have meant a
  // codegen, hence Yohan's laptop.
  const threadActivities = useContext(ThreadContext)?.activities
  const hasProposal = !!threadActivities?.some(
    (activity) => activity.type === Thread_Activity_Type_Enum.Proposal
  )
  // Only the way in is closed. A tension already at the Role level keeps its way
  // out, otherwise inherited data could never be repaired.
  const isRoleLockedByProposal =
    hasProposal && visibility !== Visibility_Enum.Role
  const decidingCircleNames = useCanPropose(circleId).decidingCircleNames

  const onSubmit = handleSubmit(async (values) => {
    if (!orgId || !currentMember) return
    if (thread) {
      // Update thread
      await updateThread({
        variables: { id: thread.id, values: values },
      })

      // Update extra members
      for (const memberId of extraMembersIds) {
        if (thread.extra_members.some((m) => m.memberId === memberId)) continue
        // Create extra member if added
        await createThreadExtraMember({
          variables: {
            values: { threadId: thread.id, memberId },
          },
        })
      }
      for (const member of thread.extra_members) {
        if (extraMembersIds.includes(member.memberId)) continue
        // Delete extra member if removed
        await deleteThreadExtraMember({
          variables: { id: member.id },
        })
      }
    } else {
      // Create thread
      const { data } = await createThread({
        variables: {
          values: {
            orgId,
            // The form carries the member now: at creation it holds the author
            // (REU89-D13), so there is nothing left to default here.
            ...values,
            // Add extra members
            extra_members: {
              data: extraMembersIds.map((id) => ({
                memberId: id,
              })),
            },
          },
        },
      })
      const createdThreadId = data?.insert_thread_one?.id
      if (!createdThreadId) return

      track('thread_created', {
        visibility: values.visibility,
        extraMembers: extraMembersIds.length,
      })

      if (onCreate) {
        onCreate(createdThreadId)
      } else {
        // Go to thread page
        navigateOrg(`threads/${createdThreadId}`)
      }
    }
    modalProps.onClose()
  })

  return (
    <FormProvider {...formMethods}>
      <Modal size="xl" {...modalProps}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={onSubmit}>
            <ModalHeader>
              {t(
                thread
                  ? 'ThreadEditModal.headingEdit'
                  : 'ThreadEditModal.headingCreate'
              )}
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <VStack spacing={10} align="stretch">
                {!thread && <MeetingOriginBanner />}

                {intro && (
                  <Alert status="info">
                    <AlertIcon />
                    <AlertDescription>{intro}</AlertDescription>
                  </Alert>
                )}

                <FormControl isInvalid={!!errors.type}>
                  <FormLabel>{t('ThreadEditModal.type')}</FormLabel>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <ThreadTypeButtons
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </FormControl>

                <FormControl isInvalid={!!errors.title}>
                  <FormLabel>{t('ThreadEditModal.title')}</FormLabel>
                  <Input
                    {...register('title')}
                    placeholder={t('ThreadEditModal.titlePlaceholder')}
                    autoFocus
                  />
                </FormControl>

                <CircleFormController
                  defaultPerimeter="myRoles"
                  writableOnly
                  visibility={visibility}
                />

                <Collapse in={!!circleId}>
                  <ParticipantsCircleExtraMembers
                    circleId={circleId}
                    membersIds={extraMembersIds}
                    onMembersIdsChange={setExtraMembersIds}
                  />
                </Collapse>

                {/* The member the tension is attributed to, correctable
                    afterwards (REU89-D13). Shown in edition only: at creation
                    the author carries their own tension, and adding a selector
                    everywhere would widen the ticket for nothing. */}
                {thread && (
                  <FormControl isInvalid={!!errors.initiatorMemberId}>
                    <FormLabel>{t('ThreadEditModal.member')}</FormLabel>
                    <Controller
                      name="initiatorMemberId"
                      control={control}
                      render={({ field }) =>
                        field.value ? (
                          <MemberByIdButton
                            id={field.value}
                            onClick={() => field.onChange('')}
                          />
                        ) : (
                          <MemberSearchButton
                            variant="outline"
                            onSelect={field.onChange}
                          >
                            {t('ThreadEditModal.memberPlaceholder')}
                          </MemberSearchButton>
                        )
                      }
                    />
                  </FormControl>
                )}

                {!thread && (
                  <FormControl>
                    <FormLabel>{t('ThreadEditModal.status')}</FormLabel>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <ThreadStatusMenu
                          size="md"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </FormControl>
                )}

                <Controller
                  name="visibility"
                  control={control}
                  render={({ field }) => (
                    <VisibilityField
                      objectType="thread"
                      circleId={circleId}
                      value={field.value}
                      onChange={field.onChange}
                      disabledLevels={
                        isRoleLockedByProposal ? [Visibility_Enum.Role] : []
                      }
                    />
                  )}
                />

                <Collapse in={!isVisibilityAllowed}>
                  <Alert status="warning">
                    <AlertIcon />
                    <AlertDescription>
                      {t('VisibilityField.notAllowed')}
                    </AlertDescription>
                  </Alert>
                </Collapse>

                <Collapse in={isRoleLockedByProposal}>
                  <Alert status="info">
                    <AlertIcon />
                    <AlertDescription>
                      {t('ThreadEditModal.roleRefusedProposal', {
                        circles: decidingCircleNames.join(' · '),
                        count: decidingCircleNames.length,
                      })}
                    </AlertDescription>
                  </Alert>
                </Collapse>

                <Box textAlign="right" mt={2}>
                  <Button
                    colorScheme="blue"
                    type="submit"
                    isDisabled={!isVisibilityAllowed || !type}
                  >
                    {t(thread ? 'common.save' : 'common.create')}
                  </Button>
                </Box>
              </VStack>
            </ModalBody>
          </form>
        </ModalContent>
      </Modal>
    </FormProvider>
  )
}
